/* eslint-disable @typescript-eslint/indent */
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { omit } from "lodash";
import { forkJoin, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DropdownOption, DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { yesNoOptions } from "../../../../shared/constants/components";
import { pattern } from "../../../../shared/constants/patterns";
import { UserInfo, UserRights, UserTypes } from "../../../../shared/types/user-info";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { User } from "../user.type";

@Injectable()
export class CreateUserService {
  constructor(private readonly partnerService: PartnerService, private readonly companyService: CompanyService) {}

  getPartner(partnerId: string): Observable<DropdownOption[]> {
    return this.partnerService.getPartner(partnerId).pipe(map((r: Partner) => [{ label: r.name, value: r.id }]));
  }

  getPartners(query?: { id?: string; name?: string }): Observable<DropdownOption[]> {
    if (query?.id) {
      const getPartner = this.partnerService
        .getPartner(query.id)
        .pipe<DropdownOption<Partner>>(map((r: Partner) => ({ label: r.name, value: r.id })));

      const getPartners = this.partnerService
        .getPartners({ where: { ...setFilterCondition(omit(query, "id"), "$cont"), id: { $not: query.id } } })
        .pipe(
          map((r: PaginatedResponse<Partner>) =>
            r.items.map<DropdownOption<Partner>>((c: Partner) => ({ label: c.name, value: c.id })),
          ),
        );

      return forkJoin([getPartner, getPartners]).pipe(
        map((res) => {
          return res.reduce<DropdownOption<Partner>[]>((acc, current) => acc.concat(current), []);
        }),
      );
    }

    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ label: c.name, value: c.id }))));
  }

  getCompanies(query?: { id?: string; partnerId?: string; name?: string }): Observable<DropdownOption[]> {
    /** @workaround to populate company dropdown with given companyId if its not included in first request due to pagination */
    if (query?.id && query?.partnerId) {
      const getCompany = this.companyService
        .getCompany(query.id)
        .pipe<DropdownOption<Company>>(
          map((company: Company) => ({ label: company.name, value: company.id, data: company })),
        );

      const getCompanies = this.companyService
        .getCompanies({ where: { ...setFilterCondition(omit(query, "id"), "$cont"), id: { $not: query.id } } })
        .pipe(
          map((r: PaginatedResponse<Company>) =>
            r.items.map<DropdownOption<Company>>((company: Company) => ({
              label: company.name,
              value: company.id,
              data: company,
            })),
          ),
        );

      return forkJoin([getCompany, getCompanies]).pipe(
        map((res) => {
          return res
            .reduce<DropdownOption<Company>[]>((_k, _v) => _k.concat(_v), [])
            .filter((company) => company.data.partnerId === query?.partnerId);
        }),
      );
    }

    return this.companyService
      .getCompanies({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ label: c.name, value: c.id }))));
  }

  generateQuestions(options: {
    userToEdit?: User;
    currentUser: UserInfo;
    hidePartner?: boolean;
    hideCompany?: boolean;
    skipPartner?: boolean;
    skipCompany?: boolean;
    disableEmail?: boolean;
    company?: Company;
    partner?: Partner;
    isEdit?: boolean;
    isSalesRep?: boolean;
    isCWRep?: boolean;
    hideIsFutureExpirationNotificationEnabled?: boolean;
  }): Observable<QuestionBase<string | boolean>[]> {
    const userToUpdateIsPartnerAdmin =
      options.userToEdit?.type === UserTypes.Partner &&
      (options.userToEdit?.right === UserRights.Admin || options.userToEdit?.right === UserRights.Manager);
    const hideIsFutureExpirationNotificationEnabled =
      options?.hideIsFutureExpirationNotificationEnabled || !userToUpdateIsPartnerAdmin;

    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        value: options.userToEdit?.firstName,
        key: "firstName",
        label: "First Name",
        required: true,
        order: 1,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "First Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "First Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "First Name is <strong>too long</strong>",
          },
        ],
      }),

      new TextboxQuestion({
        value: options.userToEdit?.lastName,
        key: "lastName",
        label: "Last Name",
        required: true,
        order: 2,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Last Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Last Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Last Name is <strong>too long</strong>",
          },
        ],
      }),

      new TextboxQuestion({
        value: options.userToEdit?.email,
        key: "email",
        label: "Email",
        required: true,
        disabled: options.disableEmail,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.email),
            message: "Email is <strong>not valid</strong>",
          },
        ],
        order: 3,
      }),

      new TextboxQuestion({
        value: options.userToEdit?.phone,
        key: "phone",
        label: "Phone",
        order: 4,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Phone is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Phone is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Phone is <strong>too long</strong>",
          },
        ],
      }),

      new DropdownQuestion({
        value: options.userToEdit?.type,
        key: "type",
        label: "User Type",
        hide: options.currentUser.isCompany(),
        disabled: options.isEdit && !options.currentUser.hasRight(["admin"]),
        required: !options.currentUser.isCompany(),
        order: 5,
        options: this.getUserTypeOptions(options.currentUser),
      }),

      new DropdownQuestion({
        value: options.userToEdit?.right,
        key: "right",
        label: "User Right",
        required: true,
        disabled: options.isEdit && !options.currentUser.hasRight(["admin"]),
        order: 6,
        ...this.getRightOptions(options.currentUser),
      }),

      !options?.skipPartner &&
        this.createPartnerDropdown({
          hidePartner: options.hidePartner || options.currentUser.isPartner() || options.currentUser.isCompany(),
          value: options.userToEdit?.partnerId || options.userToEdit?.company?.partnerId,
          partner: options?.partner,
          isEdit: options?.isEdit,
          currentUser: options?.currentUser,
        }),

      options?.isSalesRep &&
        this.createIsRepresentativeDropdown({
          key: "isSalesRep",
          label: "Is Sales Rep",
          value: options.userToEdit?.isSalesRep,
          disabled: !(options.currentUser.isGlobalAdmin() || options.currentUser.isPartnerAdmin()),
        }),

      options?.isCWRep &&
        this.createIsRepresentativeDropdown({
          key: "isCWRep",
          label: "Is Cwm Rep",
          value: options.userToEdit?.isCWRep,
          disabled: !options.currentUser.isGlobalAdmin(),
        }),

      !options?.skipCompany &&
        this.createCompanyDropdown({
          hideCompany:
            options.hideCompany || options.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          value:
            options.userToEdit?.company?.id ||
            (options.currentUser?.isCompany() &&
              options.currentUser?.hasManyCompanies() &&
              (options.currentUser.selectedCompanyId || options.currentUser.companyId)),
          isEdit: options?.isEdit,
          currentUser: options?.currentUser,
          initialPartnerId: options?.partner?.id,
        }),

      !hideIsFutureExpirationNotificationEnabled &&
        this.createIsFutureExpirationNotification({
          key: "isFutureExpirationNotificationEnabled",
          label: "Expiration Email Notifications",
          value: options.userToEdit?.isFutureExpirationNotificationEnabled,
          userToEdit: options.userToEdit,
        }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  generateQuestionsEditUserInformation(options: {
    userToEdit?: User;
    disableEmail?: boolean;
    currentUser: UserInfo;
  }): Observable<QuestionBase<string | boolean>[]> {
    const questionsEditUserInformation: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        value: options.userToEdit?.firstName,
        key: "firstName",
        label: "First Name",
        required: true,
        order: 1,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "First Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "First Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "First Name is <strong>too long</strong>",
          },
        ],
      }),

      new TextboxQuestion({
        value: options.userToEdit?.lastName,
        key: "lastName",
        label: "Last Name",
        required: true,
        order: 2,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Last Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Last Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Last Name is <strong>too long</strong>",
          },
        ],
      }),

      new TextboxQuestion({
        value: options.userToEdit?.email,
        key: "email",
        label: "Email",
        required: true,
        disabled: options.disableEmail,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.email),
            message: "Email is <strong>not valid</strong>",
          },
        ],
        order: 3,
      }),

      new TextboxQuestion({
        value: options.userToEdit?.phone,
        key: "phone",
        label: "Phone",
        order: 4,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Phone is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Phone is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Phone is <strong>too long</strong>",
          },
        ],
      }),

      new DropdownQuestion({
        value: options.userToEdit?.isMFAEnabled,
        key: "isMFAEnabled",
        label: "MFA Enabled",
        options: [...yesNoOptions],
        disabled: !options.currentUser.isGlobalAdmin(),
        ...(!options.currentUser.isGlobalAdmin() && {
          iconTooltipText: `To turn MFA on or off, please email ${environment.supportEmail}`,
        }),
        required: true,
        order: 5,
      }),
    ];

    return of(questionsEditUserInformation.filter((field) => field).sort((a, b) => a.order - b.order));
  }
  generateQuestionsEditUserPermission(options: {
    user?: User;
    currentUser: UserInfo;
    hidePartner?: boolean;
    hideCompany?: boolean;
    skipPartner?: boolean;
    skipCompany?: boolean;
    disableEmail?: boolean;
    company?: Company;
    partner?: Partner;
    isEdit?: boolean;
    isSalesRep?: boolean;
    isCWRep?: boolean;
  }): Observable<QuestionBase<string | boolean>[]> {
    const questionsEditUserPermission: QuestionBase<string | boolean>[] = [
      new DropdownQuestion({
        value: options.user?.type,
        key: "type",
        label: "User Type",
        hide: options.currentUser.isCompany(),
        disabled: options.isEdit && !options.currentUser.hasRight(["admin"]),
        required: !options.currentUser.isCompany(),
        order: 5,
        options: this.getUserTypeOptions(options.currentUser),
      }),

      new DropdownQuestion({
        value: options.user?.right,
        key: "right",
        label: "User Right",
        required: true,
        disabled: options.isEdit && !options.currentUser.hasRight(["admin"]),
        order: 6,
        ...this.getRightOptions(options.currentUser),
      }),

      !options?.skipPartner &&
        this.createPartnerDropdown({
          hidePartner: options.hidePartner || options.currentUser.isPartner() || options.currentUser.isCompany(),
          value: options.user?.partnerId || options.user?.company?.partnerId,
          partner: options?.partner,
          isEdit: options?.isEdit,
          currentUser: options?.currentUser,
        }),

      options?.isSalesRep &&
        this.createIsRepresentativeDropdown({
          key: "isSalesRep",
          label: "Is Sales Rep",
          value: options.user?.isSalesRep,
          disabled: !(options.currentUser.isGlobalAdmin() || options.currentUser.isPartnerAdmin()),
        }),

      options?.isCWRep &&
        this.createIsRepresentativeDropdown({
          key: "isCWRep",
          label: "Is Cwm Rep",
          value: options.user?.isCWRep,
          disabled: !options.currentUser.isGlobalAdmin(),
        }),

      !options?.skipCompany &&
        this.createCompanyDropdown({
          hideCompany:
            options.hideCompany || options.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          value:
            options.user?.company?.id ||
            (options.currentUser?.isCompany() &&
              options.currentUser?.hasManyCompanies() &&
              (options.currentUser.selectedCompanyId || options.currentUser.companyId)),
          isEdit: options?.isEdit,
          currentUser: options?.currentUser,
          initialPartnerId: options?.partner?.id,
        }),
    ];

    return of(questionsEditUserPermission.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  generateQuestionsEditUserNotification(options: { userToEdit?: User }): Observable<QuestionBase<string | boolean>[]> {
    const questionsEditUserNotification: QuestionBase<string | boolean>[] = [
      this.createIsFutureExpirationNotification({
        key: "isFutureExpirationNotificationEnabled",
        label: "Expiration Email Notifications",
        value: options.userToEdit?.isFutureExpirationNotificationEnabled,
        userToEdit: options.userToEdit,
      }),
    ];

    return of(questionsEditUserNotification.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createPartnerDropdown(options?: {
    hidePartner?: boolean;
    value?: string;
    partner?: Partner;
    isEdit?: boolean;
    currentUser?: UserInfo;
  }): DropdownQuestion {
    return new DropdownQuestion({
      value: options?.value,
      key: "partnerId",
      label: "Partner",
      currentOption: {
        label: options.partner?.name,
        value: options.partner?.id,
      },
      required: !options?.hidePartner,
      hide: options?.hidePartner,
      disabled: options?.isEdit && !options?.currentUser?.hasRight(["admin"]),
      order: 7,
      asyncOptions:
        !options?.hidePartner &&
        options?.value &&
        this.getPartners({
          ...(options?.value && { id: options?.value }),
        }),
      dependsOn: {
        key: "type",
        callback: (key) =>
          key.parentValue !== UserTypes.Global &&
          !options?.hidePartner &&
          this.getPartners({
            ...(options?.value && { id: options?.value }),
          }),
      },

      search: {
        filter: {
          callback: ({ value: name }) => !options?.hidePartner && this.getPartners({ name }),
        },
      },
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createIsRepresentativeDropdown(options: {
    key: "isSalesRep" | "isCWRep";
    label: string;
    disabled?: boolean;
    value?: boolean;
  }): DropdownQuestion {
    return new DropdownQuestion({
      value: options?.value ? true : false,
      key: options.key,
      label: options.label,
      options: [...yesNoOptions],
      hide: false,
      required: false,
      disabled: options.disabled,
      order: 8,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createIsFutureExpirationNotification(options: {
    key: "isFutureExpirationNotificationEnabled";
    label: string;
    disabled?: boolean;
    value?: boolean;
    userToEdit?: User;
  }): DropdownQuestion {
    return new DropdownQuestion({
      value: options?.value ? true : false,
      key: options.key,
      label: options.label,
      options: [...yesNoOptions],
      hide: false,
      required: false,
      disabled: options.disabled,
      iconTooltipText: this.getIconTooltipText(options?.userToEdit),
      order: 11,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createCompanyDropdown(options?: {
    hideCompany?: boolean;
    company?: Company;
    value?: string;
    partnerId?: string;
    isEdit?: boolean;
    currentUser?: UserInfo;
    initialPartnerId?: string;
  }): DropdownQuestion {
    return new DropdownQuestion({
      value: options?.value,
      key: "companyId",
      label: "Company",
      currentOption: {
        label: options.company?.name,
        value: options.company?.id,
      },
      required: !options?.hideCompany,
      hide: options?.hideCompany,
      ignoreInSubmit: options.currentUser?.isCompany(),
      disabled:
        (options?.isEdit && !options?.currentUser?.hasRight(["admin"])) || options.currentUser?.isCompanyAdmin(),
      order: 9,
      asyncOptions:
        !options?.hideCompany &&
        (options?.value || options?.partnerId) &&
        this.getCompanies({
          ...(options?.value && { id: options?.value }),
          ...(options?.partnerId && { partnerId: options?.partnerId }),
        }),
      dependsOn: {
        key: "partnerId",
        callback: ({ parentValue: parentPartnerId }) => {
          const partnerId = options?.initialPartnerId
            ? options.initialPartnerId !== parentPartnerId
              ? parentPartnerId
              : options.initialPartnerId
            : parentPartnerId;
          return (
            !options?.hideCompany &&
            this.getCompanies({
              ...(options?.value && { id: options?.value }),
              ...(parentPartnerId && { partnerId }),
            })
          );
        },
      },
      search: {
        filter: {
          parentKey: "partnerId",
          callback: ({ parentValue: partnerId, value: name }) =>
            !options?.hideCompany && this.getCompanies({ ...(partnerId && { partnerId }), ...(name && { name }) }),
        },
      },
    });
  }

  /**
   * Get the dropdown options to populate on the user type dropdown
   * @returns types list
   */
  private getUserTypeOptions(currentUser: UserInfo): DropdownOption[] {
    let options: DropdownOption[] = [
      { label: "Global", value: UserTypes.Global },
      { label: "Partner", value: UserTypes.Partner },
      { label: "Company", value: UserTypes.Company },
    ];
    if ((currentUser.isPartner() && currentUser.hasRight(["manager"])) || currentUser.isCompanyAdmin()) {
      options = options.slice(1);
      options.find((opt) => opt.value === UserTypes.Partner).disabled = true;
    } else if (!currentUser.isGlobal() || currentUser.isCWRep) {
      options = options.slice(1);
    }

    return options;
  }

  /**
   * Get the dropdown options to populate on the user right dropdown
   * @param currentUser logged user
   * @returns rights list
   */
  private getUserRightOptions(currentUser: UserInfo, type: string): DropdownOption[] {
    let options: DropdownOption[] = [
      { label: "Admin", value: UserRights.Admin },
      { label: "Manager", value: UserRights.Manager },
      { label: "Viewer", value: UserRights.Viewer },
    ];

    if (type === UserTypes.Global && !currentUser.isGlobalAdmin()) {
      options = options.slice(1);
    }

    return options;
  }

  private getRightOptions(currentUser: UserInfo) {
    const options: DropdownOption[] = [
      { label: "Admin", value: UserRights.Admin },
      { label: "Manager", value: UserRights.Manager },
      { label: "Viewer", value: UserRights.Viewer },
    ];
    if (currentUser.isCompany()) {
      return { options };
    }
    return {
      dependsOn: {
        key: "type",
        callback: ({ parentValue: type }) => of(this.getUserRightOptions(currentUser, type)),
      },
    };
  }

  private getIconTooltipText(userToEdit: User): string {
    const isPartnerAdminOrManager =
      userToEdit.type === UserTypes.Partner && (UserRights.Admin || userToEdit.right === UserRights.Manager);

    const isGlobalAdmin = userToEdit.type === UserTypes.Global && userToEdit.right === UserRights.Admin;

    if (isPartnerAdminOrManager) {
      return "The user will receive notifications for the contracts expiring only in the assigned companies";
    } else if (isGlobalAdmin) {
      return "The user will receive notifications for the contracts expiring only in the assigned partners";
    }
    return;
  }
}
